import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Mail, User as UserIcon, MessageCircle, Edit2, Check, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import AddUserModal from './AddUserModal';

interface UserRow {
  id: string;
  email: string;
  full_name?: string;
  role?: string;
  phone_number?: string | null;
  created_at?: string;
  updated_at?: string;
  last_sign_in_at?: string | null;
}

const UsersPage: React.FC = () => {
  const { user } = useSupabaseAuth();
  const [rows, setRows] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openAdd, setOpenAdd] = useState(false);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [notifyPrefs, setNotifyPrefs] = useState<Record<string, { email: boolean; whatsapp: boolean }>>({});
  const [editingPhoneId, setEditingPhoneId] = useState<string | null>(null);
  const [editingPhoneValue, setEditingPhoneValue] = useState<string>('');

  useEffect(() => {
    const load = async () => {
      if (!user?.id) return;
      setLoading(true);
      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('customer_id')
          .eq('id', user.id)
          .single();
        const cid = profile?.customer_id || null;
        setCustomerId(cid);

        if (!cid) {
          setRows([]);
          return;
        }

        // Prefer secured RPC that enforces tenant filtering server-side
        try {
          const { data: rpcData, error: rpcError } = await supabase
            .rpc('get_my_customer_users');
          if (rpcError) throw rpcError;
          if (rpcData && Array.isArray(rpcData)) {
            const mapped = (rpcData as any[]).map(u => ({
              id: u.id,
              email: u.email,
              full_name: u.full_name,
              role: u.role,
              phone_number: u.phone_number,
              created_at: u.created_at,
              updated_at: u.updated_at,
              last_sign_in_at: u.last_sign_in_at ?? u.user_last_sign_in_at ?? u.last_signin_at ?? null
            })) as UserRow[];
            setRows(mapped);
            return;
          }
        } catch (_e) {
          // fallback: basic version without last_sign_in_at
          try {
            const { data: basicData } = await supabase.rpc('get_my_customer_users_basic');
            if (basicData && Array.isArray(basicData)) {
              const mapped = (basicData as any[]).map(u => ({
                id: u.id,
                email: u.email,
                full_name: u.full_name,
                role: u.role,
                phone_number: u.phone_number,
                created_at: u.created_at,
                updated_at: u.updated_at,
                last_sign_in_at: u.last_sign_in_at ?? null
              })) as UserRow[];
              setRows(mapped);
              return;
            }
          } catch {}
        }

        // Fallback: direct select (RLS may return only self)
        const { data, error } = await supabase
          .from('user_profiles')
          .select('id, email, full_name, role, phone_number, created_at, updated_at, last_sign_in_at')
          .eq('customer_id', cid)
          .order('created_at', { ascending: false });
        if (error) throw error;
        setRows((data || []) as UserRow[]);
      } catch (e) {
        console.error('Failed to load users:', e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  // Initialize local notification preferences when rows/customer change
  useEffect(() => {
    if (!customerId) return;
    (async () => {
      // Start with local defaults to keep UI responsive
      try {
        const key = `notify_prefs_${customerId}`;
        const stored = localStorage.getItem(key);
        const parsed: Record<string, { email: boolean; whatsapp: boolean }> = stored ? JSON.parse(stored) : {};
        const nextLocal: Record<string, { email: boolean; whatsapp: boolean }> = { ...parsed };
        rows.forEach(r => {
          if (!nextLocal[r.id]) {
            nextLocal[r.id] = { email: false, whatsapp: false };
          }
        });
        setNotifyPrefs(nextLocal);
      } catch {
        setNotifyPrefs({});
      }
      // Then hydrate from DB for authoritative values (entire customer)
      try {
        const { data, error } = await supabase.rpc('get_customer_user_notification_prefs');
        if (!error && Array.isArray(data)) {
          const byUser: Record<string, { email: boolean; whatsapp: boolean }> = {};
          (data as any[]).forEach((row) => {
            if (row.user_id) {
              byUser[row.user_id] = {
                email: !!row.email_enabled,
                whatsapp: !!row.whatsapp_enabled
              };
            }
          });
          const merged: Record<string, { email: boolean; whatsapp: boolean }> = {};
          rows.forEach((r) => {
            merged[r.id] = byUser[r.id] ?? { email: false, whatsapp: false };
          });
          setNotifyPrefs(merged);
          try {
            const key = `notify_prefs_${customerId}`;
            localStorage.setItem(key, JSON.stringify(merged));
          } catch {}
        }
      } catch {
        // ignore RPC failure; UI already has local defaults
      }
    })();
  }, [rows, customerId]);

  // Enrich "Last Active" for the currently logged-in user from auth session
  useEffect(() => {
    (async () => {
      if (!user?.id || rows.length === 0) return;
      try {
        const { data } = await supabase.auth.getUser();
        const authUser = data?.user as any;
        const sessionLast = authUser?.last_sign_in_at || authUser?.lastSignInAt || authUser?.last_signin_at;
        if (sessionLast) {
          setRows(prev => {
            let changed = false;
            const next = prev.map(r => {
              if (r.id === user.id && r.last_sign_in_at !== sessionLast) {
                changed = true;
                return { ...r, last_sign_in_at: sessionLast };
              }
              return r;
            });
            return changed ? next : prev;
          });
        }
      } catch {}
    })();
  }, [user?.id, rows]);

  const updatePref = (userId: string, channel: 'email' | 'whatsapp', value: boolean) => {
    setNotifyPrefs(prev => {
      const next = {
        ...prev,
        [userId]: { ...(prev[userId] || { email: false, whatsapp: false }), [channel]: value }
      } as Record<string, { email: boolean; whatsapp: boolean }>;
      if (customerId) {
        const key = `notify_prefs_${customerId}`;
        try {
          localStorage.setItem(key, JSON.stringify(next));
          showToast('success', 'Notification preferences saved');
        } catch {}
      }
      // Persist to DB for the selected user row (same-customer authorization enforced in RPC)
      const prefs = next[userId] || { email: false, whatsapp: false };
      (async () => {
        try {
          const { error } = await supabase.rpc('set_user_notification_prefs', {
            p_target_user_id: userId,
            p_email: prefs.email,
            p_whatsapp: prefs.whatsapp
          });
          if (error) showToast('error', error.message);
        } catch (e: any) {
          showToast('error', e?.message || 'Failed to save');
        }
      })();
      return next;
    });
  };

  const formatTz = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'short',
        timeStyle: 'medium',
        timeZone: 'Asia/Jerusalem'
      }).format(d);
    } catch {
      return d.toLocaleString();
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return rows;
    return rows.filter(r =>
      r.email.toLowerCase().includes(q) ||
      (r.full_name || '').toLowerCase().includes(q) ||
      (r.role || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const showToast = (type: 'success' | 'error', message: string) => {
    let root = document.getElementById('users-toast-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'users-toast-root';
      root.className = 'space-y-2';
      // Inline styles to avoid any Tailwind purge issues
      Object.assign(root.style, {
        position: 'fixed',
        top: '16px',
        right: '16px',
        zIndex: '9999',
        pointerEvents: 'none'
      } as CSSStyleDeclaration);
      document.body.appendChild(root);
    }
    // Prevent duplicate stacks in StrictMode by clearing previous toast
    while (root.firstChild) root.removeChild(root.firstChild);
    const el = document.createElement('div');
    el.className = `rounded-lg shadow-lg px-4 py-3 text-sm text-white ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`;
    (el.style as any).pointerEvents = 'auto';
    el.textContent = message;
    root.appendChild(el);
    setTimeout(() => {
      el.style.transition = 'opacity 300ms ease';
      el.style.opacity = '0';
      setTimeout(() => {
        if (root && el.parentElement === root) {
          root.removeChild(el);
        }
      }, 300);
    }, 2500);
  };

  const startEditPhone = (u: UserRow) => {
    setEditingPhoneId(u.id);
    setEditingPhoneValue(u.phone_number || '');
  };

  const cancelEditPhone = () => {
    setEditingPhoneId(null);
    setEditingPhoneValue('');
  };

  const savePhone = async (userId: string) => {
    const value = editingPhoneValue.trim();
    if (value && !/^\+?[\d\s-]{6,}$/.test(value)) {
      showToast('error', 'Invalid phone number');
      return;
    }
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ phone_number: value || null })
        .eq('id', userId);
      if (error) throw error;
      setRows(prev => prev.map(r => (r.id === userId ? { ...r, phone_number: value || null } : r)));
      setEditingPhoneId(null);
      setEditingPhoneValue('');
      showToast('success', 'Phone updated');
    } catch (e: any) {
      showToast('error', e?.message || 'Failed to update phone');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
          <p className="text-sm text-gray-500">Users in your organization</p>
        </div>
        <button
          onClick={() => setOpenAdd(true)}
          className="inline-flex items-center gap-2 bg-black text-traqpack-yellow px-4 py-2 rounded-lg hover:bg-gray-900"
        >
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full px-3 py-2 border border-gray-200 rounded-lg"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col style={{ width: '32%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '5%' }} />
          </colgroup>
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left p-3 font-medium text-gray-600">User</th>
              <th className="text-center p-3 font-medium text-gray-600">Role</th>
              <th className="text-center p-3 font-medium text-gray-600">Phone</th>
              <th className="text-center p-3 font-medium text-gray-600">Notifications</th>
              <th className="text-center p-3 font-medium text-gray-600">Last Active</th>
              <th className="text-center p-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-gray-500">Loading...</td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-gray-500">No users found</td>
              </tr>
            )}
            {!loading && filtered.map((u) => (
              <tr key={u.id} className="border-b last:border-0">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <UserIcon className="w-4 h-4 text-gray-500" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{u.full_name || u.email.split('@')[0]}</div>
                      <div className="text-gray-500">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-gray-700 text-center">{u.role || 'customer'}</td>
                <td className="p-3 text-gray-700 text-center">
                  {editingPhoneId === u.id ? (
                    <div className="flex items-center justify-center gap-2">
                      <input
                        value={editingPhoneValue}
                        onChange={(e) => setEditingPhoneValue(e.target.value)}
                        placeholder="+1 555 123 4567"
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                        autoFocus
                      />
                      <button
                        className="p-1 rounded text-green-600 hover:bg-green-50"
                        title="Save"
                        onClick={() => savePhone(u.id)}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        className="p-1 rounded text-gray-500 hover:bg-gray-100"
                        title="Cancel"
                        onClick={cancelEditPhone}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span>{u.phone_number || '-'}</span>
                      <button
                        className="p-1 rounded text-gray-500 hover:bg-gray-100"
                        title="Edit phone"
                        onClick={() => startEditPhone(u)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </td>
                {/* Notifications column */}
                <td className="p-3 text-gray-700">
                  <div className="flex items-center justify-center gap-6">
                    <label className={`inline-flex items-center gap-1 ${!u.email ? 'opacity-50' : ''}`} title={u.email ? `Email alerts to ${u.email}` : 'No email available'}>
                      <input
                        type="checkbox"
                        disabled={!u.email}
                        checked={!!notifyPrefs[u.id]?.email}
                        onChange={(e) => updatePref(u.id, 'email', e.target.checked)}
                      />
                      <Mail className="w-4 h-4 text-gray-600" />
                      <span className="text-xs">Email</span>
                    </label>
                    <label className={`inline-flex items-center gap-1 ${!u.phone_number ? 'opacity-50' : ''}`} title={u.phone_number ? `WhatsApp alerts to ${u.phone_number}` : 'No phone number available'}>
                      <input
                        type="checkbox"
                        disabled={!u.phone_number}
                        checked={!!notifyPrefs[u.id]?.whatsapp}
                        onChange={(e) => updatePref(u.id, 'whatsapp', e.target.checked)}
                      />
                      <MessageCircle className="w-4 h-4 text-gray-600" />
                      <span className="text-xs">WhatsApp</span>
                    </label>
                    {/* Auto-saved on toggle; no explicit Save button */}
                  </div>
                </td>
                <td className="p-3 text-gray-700 text-center">{formatTz(u.last_sign_in_at || u.updated_at || u.created_at)}</td>
                <td className="p-3">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      className="p-2 rounded hover:bg-gray-100"
                      title="Resend invite"
                      onClick={async () => {
                        try {
                          const { error } = await supabase.auth.resetPasswordForEmail(u.email, {
                            redirectTo: `${window.location.origin}/reset-password`
                          });
                          if (error) throw error;
                          showToast('success', 'Invitation sent');
                        } catch (e: any) {
                          showToast('error', e?.message || 'Failed to resend invite');
                        }
                      }}
                    >
                      <Mail className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AddUserModal
        isOpen={openAdd}
        onClose={() => setOpenAdd(false)}
        onSuccess={async () => {
          // Refresh list
          if (!customerId) return;
          try {
            const { data: rpcData } = await supabase.rpc('get_my_customer_users');
            if (rpcData && Array.isArray(rpcData)) {
              const mapped = (rpcData as any[]).map(u => ({
                id: u.id,
                email: u.email,
                full_name: u.full_name,
                role: u.role,
                phone_number: u.phone_number,
                created_at: u.created_at,
                updated_at: u.updated_at,
                last_sign_in_at: u.last_sign_in_at || null
              })) as UserRow[];
              setRows(mapped);
              return;
            }
          } catch {}

          const { data } = await supabase
            .from('user_profiles')
            .select('id, email, full_name, role, phone_number, created_at, updated_at, last_sign_in_at')
            .eq('customer_id', customerId)
            .order('created_at', { ascending: false });
          setRows((data || []) as UserRow[]);
        }}
        onNotify={(type, message) => showToast(type, message)}
      />
    </div>
  );
};

export default UsersPage;


